import type { DocPage } from './types';

/**
 * Overview, getting started, concepts, roles and the end-to-end journey.
 *
 * Everything here was checked against the source. Where the product does not do
 * what a reader would reasonably expect, the page says so in a `todo` block
 * rather than describing the intention as if it were behaviour.
 */
export const GUIDE_PAGES: DocPage[] = [
  // -------------------------------------------------------------------------
  {
    slug: 'overview',
    title: 'What Chairly is',
    summary:
      'A control plane that puts a human approval step in front of anything your AI agents do to a real system.',
    section: 'start',
    keywords: ['product', 'overview', 'introduction', 'what is'],
    blocks: [
      {
        kind: 'text',
        body: 'Chairly sits between an AI agent and the systems it can change. When an agent wants to refund a customer, email a list, or delete records, the action stops here first — translated into one plain sentence, routed to whoever is allowed to sign it off, and recorded in a tamper-evident log.',
      },
      {
        kind: 'text',
        body: 'The agent does not need to know any of this happened. It calls the gateway, waits, and either gets a result or a refusal.',
      },
      { kind: 'heading', body: 'Who it is for' },
      {
        kind: 'list',
        items: [
          'AI automation agencies running workflows on behalf of clients who are not technical.',
          'The clients themselves, who get a plain-language view of what was done in their name and a say over anything risky.',
          'Solo operators automating their own business who want a brake on irreversible actions.',
        ],
      },
      { kind: 'heading', body: 'What it gives you' },
      {
        kind: 'list',
        items: [
          'A client can approve or decline an action without understanding what an API is.',
          'An agency cannot approve on a client’s behalf when policy says the client must sign. This is enforced server-side.',
          'Every action, decision and execution is written to a hash-chained audit log that detects both edits and deletions.',
          'A retried workflow rejoins its original approval instead of queueing a second one.',
          'Your own branding on the client-facing portal and emails, from the Startup plan up.',
        ],
      },
      { kind: 'heading', body: 'What it is not' },
      {
        kind: 'text',
        body: 'Chairly does not build or host your agents. It does not replace n8n, Make, CrewAI or your own code. It is the approval and audit layer those tools call before they touch anything that matters.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'concepts',
    title: 'Core concepts',
    summary: 'Agency, client workspace, approval policy, and the audit chain — the four ideas everything else rests on.',
    section: 'concepts',
    keywords: ['agency', 'workspace', 'policy', 'audit', 'glossary', 'terminology'],
    blocks: [
      { kind: 'heading', body: 'Agency' },
      {
        kind: 'text',
        body: 'The top-level tenant. One agency is created automatically for every account at signup — you are an agency of one until you add clients. The agency owns the plan, the billing relationship, the branding, and the API keys.',
      },
      { kind: 'heading', body: 'Client workspace' },
      {
        kind: 'text',
        body: 'One of your clients. It holds their contact address, their connected tools, and their approval policy. Every approval belongs to exactly one workspace, and a workspace belongs to exactly one agency. Your plan caps how many active workspaces you can run; archived ones keep their audit trail without occupying a paid slot.',
      },
      { kind: 'heading', body: 'Approval policy' },
      {
        kind: 'text',
        body: 'A plain JSON document per workspace deciding who signs for what. Five lanes — reading data, making changes, moving money, bulk actions, irreversible actions — each set to run automatically, agency approves, client approves, or never allow.',
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'The strictest matching rule wins. Evaluation order is: deny → per-tool override → money → bulk → irreversible → lane default.',
      },
      { kind: 'heading', body: 'Approval request' },
      {
        kind: 'text',
        body: 'One pending action. It carries a plain-language preview, the risk level, who must approve, and a resume token so a paused workflow can pick up where it left off. It moves through pending → approved → executed, or to rejected, expired or failed. Those five are terminal: nothing changes after them.',
      },
      { kind: 'heading', body: 'Audit chain' },
      {
        kind: 'text',
        body: 'An append-only log per workspace where each entry carries the hash of the one before it. Editing a past entry breaks every link after it. A per-workspace head record is compared against the last entry, so deleting the tail is detected too — a truncated log does not simply look shorter, it reads as broken.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'getting-started',
    title: 'Getting started',
    summary: 'From signing up to your first approved action, with the configuration each step actually needs.',
    section: 'start',
    keywords: ['setup', 'signup', 'onboarding', 'first', 'install', 'quickstart'],
    status: {
      state: 'partial',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
      gaps: [
        'Client portal logins require the Agency plan. Upgrading works, but activates without payment while verification is off — see Billing & plans.',
        'Outbound email needs RESEND_API_KEY on the server. Without it invites cannot be delivered.',
      ],
    },
    blocks: [
      { kind: 'heading', body: '1. Create your account' },
      {
        kind: 'steps',
        items: [
          'Sign in with Google or email at /signin. Authentication is Firebase Auth.',
          'An agency, your first client workspace and a business profile are created automatically. You do not fill in a setup wizard.',
          'You land on the workspace dashboard. The Agency tab is where the control-plane features live.',
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'New accounts start on the Developer plan: one client workspace, 100 approvals a month, no white-labelling, no client portal logins.',
      },

      { kind: 'heading', body: '2. Create a gateway API key' },
      {
        kind: 'steps',
        items: [
          'Go to Agency → Approval gateway.',
          'Enter a name that identifies where it will live, for example "n8n production".',
          'Copy the token immediately. Only its hash is stored, so it cannot be shown again.',
        ],
      },
      { kind: 'screenshot', caption: 'Agency → Approval gateway, with one active key and the reveal-once panel' },
      {
        kind: 'note',
        tone: 'warn',
        body: 'A key belongs to your agency and can only submit actions into your own client workspaces. If a key leaks, revoke it — revocation takes effect immediately and cannot be undone.',
      },

      { kind: 'heading', body: '3. Set the approval policy' },
      {
        kind: 'steps',
        items: [
          'Agency → Clients → Settings on the client you want to configure.',
          'Under "Who approves what", decide each of the five lanes.',
          'Add per-tool exceptions if one specific operation needs different handling.',
          'Save.',
        ],
      },
      {
        kind: 'text',
        body: 'The defaults are deliberately cautious: reads run automatically, changes need your agency, and money, bulk and irreversible actions go to the client.',
      },

      { kind: 'heading', body: '4. Send your first action through' },
      {
        kind: 'text',
        body: 'From n8n, Make, the SDK, or plain HTTP. The call blocks while a human decides, up to your wait budget, then hands back a resume token.',
      },
      {
        kind: 'code',
        language: 'bash',
        body: `curl -X POST https://your-api-host/v1/approvals/request \\
  -H "x-chairly-key: chairly_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "workspaceId": "<your client workspace id>",
    "connectorId": "stripe",
    "operation": "refunds.create",
    "params": { "charge": "ch_123", "amount": 4200 },
    "idempotencyKey": "refund-ch_123",
    "waitMs": 30000
  }'`,
      },
      {
        kind: 'text',
        body: 'The response is a ticket: the approval id, its state, the plain-language preview shown to the approver, and a resume token to poll with.',
      },

      { kind: 'heading', body: '5. Approve it' },
      {
        kind: 'steps',
        items: [
          'Agency-routed actions appear under Agency → Waiting on you. Approve or decline inline.',
          'Client-routed actions go to the client portal instead. Your agency cannot sign these.',
        ],
      },
      {
        kind: 'text',
        body: 'On approval the action runs immediately through the connector and the workflow that was waiting receives the result.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'workflow',
    title: 'The complete journey',
    summary: 'Signup through to a client reviewing a month of work — every stage and what happens at it.',
    section: 'start',
    keywords: ['journey', 'lifecycle', 'end to end', 'flow', 'process'],
    blocks: [
      {
        kind: 'text',
        body: 'This is the full path through the product, in order. Each stage links to the feature page that covers it in detail.',
      },
      {
        kind: 'table',
        headers: ['Stage', 'Who acts', 'What happens'],
        rows: [
          ['Join', 'Agency owner', 'Sign in. An agency, one client workspace and a business profile are provisioned automatically.'],
          ['Set up', 'Agency owner', 'Create a gateway API key. Set branding if the plan allows it.'],
          ['Onboard a client', 'Agency owner', 'Add a client workspace with their name and contact address. Capped by plan.'],
          ['Configure', 'Agency owner', 'Set the approval policy for that client: who signs for money, bulk, irreversible actions.'],
          ['Invite', 'Agency owner', 'Send the client a magic-link invite. Requires the Agency plan and a configured email provider.'],
          ['Connect', 'Agency owner', 'Point n8n, Make, the SDK or your own code at the gateway using the API key.'],
          ['Run', 'The agent', 'An agent submits an action. Policy routes it. It waits, executes, or is refused.'],
          ['Decide', 'Agency or client', 'Whoever policy names approves or declines. Nothing runs without that.'],
          ['Monitor', 'Agency owner', 'Cross-client queue, per-client cards, and the audit log with chain verification.'],
          ['Report', 'Client', 'The portal shows what ran. A monthly report is emailed on the 1st.'],
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'Nothing in this path requires the client to install anything or hold a password. Their entire credential is a single-use link sent to the address you recorded.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'roles',
    title: 'Roles & permissions',
    summary: 'Four roles, what each can reach, and the one boundary the product exists to enforce.',
    section: 'roles',
    keywords: ['permissions', 'roles', 'access', 'security', 'rbac', 'who can'],
    status: {
      state: 'shipped',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
    },
    blocks: [
      {
        kind: 'table',
        headers: ['Role', 'How they sign in', 'What they can reach'],
        rows: [
          ['agency_owner', 'Firebase Auth', 'Everything in their own agency: clients, policy, branding, API keys, billing, audit.'],
          ['agency_member', 'Firebase Auth', 'Can approve agency-routed actions. Not separately enforced beyond approval today — see the gap below.'],
          ['client_approver', 'Magic link', 'Their one workspace: overview, history, and approving client-routed actions.'],
          ['client_viewer', 'Magic link', 'Their one workspace, read only. Cannot decide anything.'],
        ],
      },

      { kind: 'heading', body: 'The boundary that matters' },
      {
        kind: 'note',
        tone: 'danger',
        body: 'When policy routes an action to the client, no agency role can approve it — agency_owner included. This is checked server-side in canRoleApprove, and a refused attempt is written to the audit log. Client approval exists precisely so the agency cannot sign in the client’s place; treating the owner as a superuser would void the product.',
      },
      {
        kind: 'text',
        body: 'The same reasoning is why a client invite is emailed to the client and never returned to the agency. The link redeems into a client_approver session, so handing it to the agency would let them become the client.',
      },

      { kind: 'heading', body: 'Tenant isolation' },
      {
        kind: 'list',
        items: [
          'No agency route accepts an agencyId. Your agency is resolved from your own signed-in identity.',
          'A gateway API key resolves to exactly one agency. Naming another agency’s workspace id returns 404, not 403 — a wrong-tenant workspace is indistinguishable from one that does not exist.',
          'A client portal session is scoped to a single workspace. There is no session that spans clients.',
        ],
      },

      { kind: 'heading', body: 'What requires approval' },
      {
        kind: 'text',
        body: 'Entirely policy-driven per workspace. Out of the box: reads run automatically, writes need the agency, and money, bulk and irreversible actions need the client. A per-tool override can tighten or loosen any single operation.',
      },
      {
        kind: 'todo',
        body: 'agency_member is defined and accepted as an approver, but there is no UI to invite one and no membership record — an agency is effectively single-user today. Adding a second agency user requires writing the role onto the user document directly.',
      },
      {
        kind: 'todo',
        body: 'There is no platform administrator role. No account can see across agencies, suspend a tenant, or inspect another agency’s data. Operational tasks of that kind are done with database access.',
      },
    ],
  },
];
